/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2025-01-22 08:02:26
 **/

import { LocalData, SessionData } from "@/storage";

/**
 * Represents the configuration options for an item.
 *
 * This type defines optional settings that can be applied to customize item behavior.
 * The options include:
 * - `useCrypto`: A boolean flag indicating whether cryptographic methods should be used.
 * - `storageType`: Specifies the type of storage to be used; either "local" or "session".
 *
 * The type is defined as a `Partial`, making all properties optional.
 */
type ConfigureItemOptions = Partial<{
	useCrypto: boolean;
	storageType: "local" | "session";
}>;

const ConfigureItemDefaultOptions: ConfigureItemOptions = {
	useCrypto: false,
	storageType: "local"
};

/**
 * A class representing a configurable item, allowing for storage, retrieval,
 * and management of configuration data with specified options and default values.
 *
 * @template T - The type of the configuration item's value. Defaults to `unknown`.
 */
export default class ConfigureItem<T = unknown> {
	#options: ConfigureItemOptions = ConfigureItemDefaultOptions;
	#value_type: string = "";

	constructor(
		public name: string,
		public readonly defaultValue: T,
		public readonly options?: ConfigureItemOptions
	) {
		this.#options = { ...ConfigureItemDefaultOptions, ...(options ? options : {}) };
		this.#value_type = typeof defaultValue;
	}

	/**
	 * Returns the default configuration options.
	 *
	 * @return {Object} The default configuration options.
	 */
	public get defaultOptions(): Partial<ConfigureItemOptions> {
		return ConfigureItemDefaultOptions;
	}

	/**
	 * Retrieves the configured name.
	 *
	 * @return {string} The configured name.
	 */
	public get config_name(): string {
		return this.name;
	}

	/**
	 * Retrieves the stored value associated with the configuration name.
	 * Converts the value to the specified type if a value type is defined.
	 *
	 * @return {T} The stored value, converted to the appropriate type if specified.
	 */
	public get read(): T {
		const value = this.storage.get(this.config_name, this.defaultValue, this.#options.useCrypto) as T;
		if (this.#value_type === "boolean") {
			return Boolean(value) as T;
		}
		if (this.#value_type === "number") {
			return Number(value) as T;
		}
		if (this.#value_type === "string") {
			return String(value) as T;
		}
		return value;
	}

	/**
	 * Sets a value to be written to the storage. If the value is not null, it is stored with optional encryption based on configuration. If the value is null, the entry is removed from storage.
	 *
	 * @param {T | null} value - The value to be written to the storage. If null, the storage entry is removed.
	 */
	public set write(value: T | null) {
		if (value !== null) {
			this.storage.set(this.config_name, value, this.#options.useCrypto);
		} else {
			this.remove();
		}
	}

	public update(value: T | null) {
		this.write = value;
	}

	/**
	 * Retrieves the storage type based on the provided option.
	 * It checks the configuration and returns the appropriate storage implementation.
	 *
	 * @return {StorageType} The storage implementation, either local or session.
	 */
	public get storage(): StorageType {
		if (this.#options.storageType === "local") {
			return LocalData;
		}
		return SessionData;
	}

	/**
	 * Removes a specific configuration from the storage based on the configuration name.
	 *
	 * @return {void} Does not return a value.
	 */
	public remove() {
		this.storage.remove(this.config_name);
	}
}
